// ** React Imports
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

// ** Third Party Imports
import { ImageIcon, toaster } from "@saleshandy/design-system";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

// ** Custom Imports
import { Images } from "../../../../../shared/app-constants";
import { accessibleOnClick } from "../../../../../shared/utils";
import { RootState } from "../../../../../store/root-reducer";
import { getPostLoadMetaRequest } from "../../../../home/extra-actions";
import { setAiColumnModalVisible, setShowAiColumnWelcomeScreen } from "../../../prospect-slice";
import AiColumns from "../../ai-col-enrichment-modal/ai-col-enrichment-modal";
import { AiColEnrichmentModalSteps } from "../../ai-col-enrichment-modal/enums";
import { OverlayTooltip } from "../../../../../shared/design-system/components/overlay";

const TriggerAiColumnButton: React.FC = () => {
    const [isAiColumnModalOpen, setIsAiColumnModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState<AiColEnrichmentModalSteps>(
      AiColEnrichmentModalSteps.ENRICH_OPTION,
    );
    const [stepData, setStepData] = useState<any>({});
    const [enableEnrichBtn, setEnableEnrichBtn] = useState<boolean>(true);
    const location = useLocation();
    const aiOpenCtx = useSelector(
      (state: RootState) => state?.prospect?.aiEnrichmentOpenContext,
    );
    const isProspectEnrichmentEnabledMeta = useSelector(
      (state: RootState) => state?.home?.isProspectEnrichmentEnabled,
    );
    const isUserEmailVerified = useSelector(
      (state: RootState) => state?.home?.verified,
    );
  
    const dispatch = useDispatch();
  
    // ** Memoized values
  
    const isProspectsKanbanView = useMemo(
      () => location?.pathname?.includes('/crm/kanban'),
  
      [location?.pathname],
    );
  
    const closeAiColumnsModal = () => {
      setIsAiColumnModalOpen(false);
      dispatch(setShowAiColumnWelcomeScreen(true));
      setEnableEnrichBtn(true);
      setCurrentStep(AiColEnrichmentModalSteps.ENRICH_OPTION);
      setStepData({});
    };
  
    const handleShowAiColumnModal = useCallback(() => {
      if (isUserEmailVerified) {
        dispatch(setShowAiColumnWelcomeScreen(true));
        setIsAiColumnModalOpen(true);
        dispatch(getPostLoadMetaRequest());
        dispatch(setAiColumnModalVisible(true));
      } else {
        setEnableEnrichBtn(false);
        toaster.error('Please verify your email to perform this action.');
        setTimeout(() => {
          setEnableEnrichBtn(true);
        }, 3000);
      }
    }, [isUserEmailVerified]);
  
    // Open modal when Redux open-context is set (URL-free flow)
    useEffect(() => {
      if (aiOpenCtx && !isAiColumnModalOpen) {
        if (isUserEmailVerified) {
          if (aiOpenCtx.step === AiColEnrichmentModalSteps.EXISTING_COLUMN) {
            setCurrentStep(AiColEnrichmentModalSteps.EXISTING_COLUMN);
            setStepData({
              [AiColEnrichmentModalSteps.EXISTING_COLUMN]: {
                templateId: aiOpenCtx.shareableId,
                fieldId: aiOpenCtx.fieldId,
                fromOutside: aiOpenCtx.fromOutside,
              },
            });
          } else {
            setCurrentStep(AiColEnrichmentModalSteps.TEMPLATE_DETAIL);
            setStepData({ shareableId: aiOpenCtx.shareableId });
          }
          setIsAiColumnModalOpen(true);
        } else {
          toaster.error('Please verify your email to perform this action.');
        }
      }
    }, [aiOpenCtx]);
  
    // Fallback: Open modal from URL param templateId (legacy flow)
    useEffect(() => {
      const queryParams = new URLSearchParams(location?.search);
      const templateId = queryParams.get('templateId');
  
      if (templateId && !isAiColumnModalOpen && !aiOpenCtx) {
        if (isUserEmailVerified) {
          setCurrentStep(AiColEnrichmentModalSteps.TEMPLATE_DETAIL);
          setStepData({ shareableId: templateId });
          setIsAiColumnModalOpen(true);
        } else {
          toaster.error('Please verify your email to perform this action.');
        }
      }
    }, [location?.search, aiOpenCtx]);
  
    return (
      <>
        {isProspectEnrichmentEnabledMeta && (
          <OverlayTooltip
            text="Enrich Prospects"
            className={!isProspectsKanbanView ? 'd-none' : ''}
          >
            <div
              className={`d-flex align-items-center enrich-prospect-button  ${
                isProspectsKanbanView ? 'ai-icon' : ''
              } ${enableEnrichBtn ? 'cr-prospect' : 'disabled'}`}
              {...(enableEnrichBtn
                ? { ...accessibleOnClick(handleShowAiColumnModal) }
                : {})}
            >
              <ImageIcon src={Images.Stars} width={16} height={16} alt="stars" />
              {!isProspectsKanbanView ? <span>Enrich Prospects</span> : <></>}
            </div>
        </OverlayTooltip>
        )}
        <AiColumns
          show={isAiColumnModalOpen}
          onClose={closeAiColumnsModal}
          initialStep={currentStep}
          initialStepData={stepData}
        />
      </>
    );
  };

export default memo(TriggerAiColumnButton);